import { find, has } from 'lodash';
import { valueReportDefinitions } from 'client/GrowthEngineering/utilities/GrowthConstants';
import ApplicationApiV2 from 'client/api/v2/ApplicationApiV2';
import AuthNPoliciesApi from 'client/api/v2/AuthNPoliciesApi';
import CommandsApi from 'client/api/v2/CommandsApi';
import Constants, { MdmVendorStates, UserRoleConsts } from 'client/util/Constants';
import DirectoryApi from 'client/api/v2/DirectoryApi';
import DirectoryGroupingConstants from 'client/util/Constants/DirectoryGroupingConstants';
import EventMetadata, { eventFields } from 'client/Insights/ActivityLog/constants/EventMetadata';
import EventTypes from 'client/Insights/ActivityLog/constants/EventTypes';
import InsightsEventsApi from 'client/api/v2/InsightsEventsApi';
import PlanApi from 'client/api/v2/PlanApi';
import PolicyApi from 'client/api/v2/PolicyApi';
import RadiusServerApi from 'client/api/v2/RadiusServerApi';
import SettingsApi from 'client/api/v2/SettingsApi';
import SubscriptionApi from 'client/api/v2/SubscriptionApi';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';
import SystemApi from 'client/api/v2/SystemApi';
import SystemFilters from 'client/components/ItemTable/CollectionFilterComponents/constants/SystemFilters';
import SystemGroupApi from 'client/api/v2/SystemGroupApi';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import SystemUserFilters from 'client/components/ItemTable/CollectionFilterComponents/constants/SystemUserFilters';
import UserGroupApi from 'client/api/v2/UserGroupApi';
import axios from 'axios';
import moment from 'moment';

const { administratorWithBilling, commandRunnerWithBilling } = UserRoleConsts;

const {
  internal,
} = MdmVendorStates;

const { macOSX } = Constants;

class ValueReportApiWrapper {
  constructor() {
    this.encryptedFilter = SystemFilters.fde(true);
    this.systemMfaFilter = SystemFilters.mfa(true);
    this.systemMdmFilter = SystemFilters.mdm(internal);
    this.totalUsers = 0;
    this.totalDevices = 0;
    this.userMfaFilter = SystemUserFilters.totpEnabled(true);
    this.orgSettings = false;
    this.reportPeriodStartDate = moment().subtract(1, 'months').startOf('month').toISOString();
    this.reportPeriodEndDate = moment().subtract(1, 'months').endOf('month').toISOString();
  }

  fetchAll = async (role) => {
    // Current date plus one day as a buffer for DI event calls to get most up-to-date event data
    this.currentISODate = moment().add(1, 'days').toISOString();

    const valueReportData = {
      overviewMetrics: [],
      securityCompliance: [],
      efficiency: [],
    };

    [this.totalUsers, this.totalDevices] = await Promise.all([
      this.fetchTotalUsersForReportPeriod(),
      this.fetchTotalDevicesForReportPeriod(),
    ]);

    const [
      userCount,
      appCount,
      deviceCount,
      commandCount,
      userGroupCount,
      policyCount,
      deviceGroupCount,
      devicesEncrypted,
      accessPolicies,
      usersAffectedByPolicies,
      passwordComplexity,
      mfaDevices,
      mfaUsers,
      mfaAuths,
      mdmDevices,
      usersActivated,
      usersSuspended,
      identitiesProvisioned,
      scheduledCommands,
    ] = await Promise.all([
      this.fetchTotalUsersDataForReportPeriod(),
      this.fetchTotalApplicationsForReportPeriod(),
      this.fetchTotalDevicesDataForReportPeriod(),
      this.fetchTotalCommandsForReportPeriod(),
      this.fetchTotalUserGroupsForReportPeriod(),
      this.fetchTotalPoliciesForReportPeriod(),
      this.fetchTotalDeviceGroupsForReportPeriod(),
      this.fetchDevicesEncrypted(),
      this.fetchConditionalPolicies(),
      this.fetchUsersInGroupsAffectedByConditionalPolicies(),
      this.fetchPasswordComplexity(),
      this.fetchDevicesInMfa(),
      this.fetchUsersInMfa(),
      this.fetchAuthsUsingMfa(),
      this.fetchDevicesInMdm(),
      this.fetchTotalUsersActivated(),
      this.fetchTotalUsersSuspended(),
      this.fetchAppIdentitiesProvisioned(),
      this.fetchScheduledCommands(),
    ]);

    valueReportData.overviewMetrics = [
      userCount,
      appCount,
      deviceCount,
      commandCount,
      userGroupCount,
      policyCount,
      deviceGroupCount,
    ];

    valueReportData.securityCompliance = [
      devicesEncrypted,
      accessPolicies,
      usersAffectedByPolicies,
      passwordComplexity,
      mfaUsers,
      mfaDevices,
      mfaAuths,
      mdmDevices,
    ];

    valueReportData.efficiency = [
      usersActivated,
      usersSuspended,
      identitiesProvisioned,
      scheduledCommands,
    ];

    valueReportData.vendorConsolidation = await this.getVendorConsolidationData(role);

    return valueReportData;
  }

  getInsightsEventsCount = async (startDate, endDate, eventTypes, additionalFilters = [], services = ['all']) => {
    if (!eventTypes) {
      throw new Error('Event type(s) required');
    }
    const request = {
      service: services,
      start_time: startDate,
      end_time: endDate,
      search_term: {
        and: [
          { [EventMetadata[eventFields.eventType].path]: eventTypes },
          ...additionalFilters,
        ],
      },
    };
    const { data } = await InsightsEventsApi.fetchCount(request);

    return parseInt(data.count, 10);
  }

  fetchTotalUsersForReportPeriod = async () => {
    const totalUsers = await SystemUserApi
      .fetch(
        null,
        3,
        null,
        null,
        null,
        [SystemUserFilters.createdOnOrBefore(moment().subtract(1, 'months').endOf('month'))],
      )
      .then(({ totalCount }) => totalCount);

    return totalUsers;
  }

  fetchTotalDevicesForReportPeriod = async () => {
    const totalDevices = await SystemApi
      .fetch(
        0,
        1,
        null,
        null,
        null,
        [SystemFilters.createdOnOrBefore(moment().subtract(1, 'months').endOf('month'))],
      )
      .then(({ totalCount }) => totalCount);

    return totalDevices;
  }

  fetchDevicesEncrypted = async () => {
    const dataObject = {
      type: valueReportDefinitions.securityCompliance.deviceEncrypted.type,
      label: valueReportDefinitions.securityCompliance.deviceEncrypted.label,
      count: 0,
      total: 0,
      definition: valueReportDefinitions.securityCompliance.deviceEncrypted.definition,
    };

    dataObject.total = this.totalDevices;

    // get systems created before period end date that are encrypted
    const { totalCount } = await SystemApi
      .fetch(
        null,
        1,
        null,
        null,
        null,
        [SystemFilters.createdOnOrBefore(moment().subtract(1, 'months').endOf('month')), this.encryptedFilter],
      );
    dataObject.count = totalCount;

    return dataObject;
  }

  // These are AuthN Conditional Policies, not device policies
  fetchConditionalPolicies = async () => {
    // there is no created date on the policy node so just use current
    const dataObject = {
      type: valueReportDefinitions.securityCompliance.conditionalPolicies.type,
      label: valueReportDefinitions.securityCompliance.conditionalPolicies.label,
      value: false,
      definition: valueReportDefinitions.securityCompliance.conditionalPolicies.definition,
    };

    const policies = await AuthNPoliciesApi.fetchAll();
    const policyTotal = policies.length;
    if (policyTotal > 0) {
      dataObject.value = true;
    }

    return dataObject;
  }

  // Gets all AuthN Policies, gets the groups/users that are in them
  // then sums them up.
  fetchUsersInGroupsAffectedByConditionalPolicies = async () => {
    const dataObject = {
      type: valueReportDefinitions.securityCompliance.conditionalPoliciesUsers.type,
      label: valueReportDefinitions.securityCompliance.conditionalPoliciesUsers.label,
      value: 0,
      definition: valueReportDefinitions.securityCompliance.conditionalPoliciesUsers.definition,
    };
    const usersAffectedByConditionalPolicies = [];
    const groupsAffectedByConditionalPolicies = [];
    let allUsersAffected = false;

    // get all authN policies with user groups
    const { totalCount, results: policies } = await AuthNPoliciesApi.fetch();
    if (totalCount > 0) {
      policies.every(policy => {
        if (policy.targets.users.inclusions.includes('all')) {
          // it's all users, return total users for org
          allUsersAffected = true;
          return false; // exit loop, it's everyone
        }
        // inclusions and exclusions are both affected
        groupsAffectedByConditionalPolicies.push(...policy.targets.userGroups.exclusions);
        groupsAffectedByConditionalPolicies.push(...policy.targets.userGroups.inclusions);
        return true;
      });
    }
    if (!allUsersAffected) {
      const uniqueGroups = [...new Set(groupsAffectedByConditionalPolicies)];

      // Loop through affected groups, get member IDs for calc of total
      await Promise.all(uniqueGroups.map(async (groupId) => {
        const resp = await axios.get(`/api/v2/usergroups/${groupId}/membership`);
        const userIds = resp.data
          .filter(user => user.id !== 'all')
          .map((user) => user.id);

        usersAffectedByConditionalPolicies.push(...userIds);
      }));

      const uniqueUsers = [...new Set(usersAffectedByConditionalPolicies)];
      dataObject.value = uniqueUsers.length;
    } else {
      dataObject.value = this.totalUsers;
    }

    return dataObject;
  }

  fetchPasswordComplexity = async () => {
    const dataObject = {
      type: valueReportDefinitions.securityCompliance.passwordComplexity.type,
      label: valueReportDefinitions.securityCompliance.passwordComplexity.label,
      value: false,
      definition: valueReportDefinitions.securityCompliance.passwordComplexity.definition,
    };

    this.orgSettings = await SettingsApi.getGlobalSettings();
    if (this.orgSettings.SETTINGS.passwordPolicy.needsLowercase
      || this.orgSettings.SETTINGS.passwordPolicy.needsUppercase
      || this.orgSettings.SETTINGS.passwordPolicy.needsNumeric
      || this.orgSettings.SETTINGS.passwordPolicy.needsSymbolic
    ) {
      dataObject.value = true;
    }

    return dataObject;
  }

  fetchDevicesInMfa = async () => {
    const dataObject = {
      type: valueReportDefinitions.securityCompliance.deviceMfa.type,
      label: valueReportDefinitions.securityCompliance.deviceMfa.label,
      value: 0,
      definition: valueReportDefinitions.securityCompliance.deviceMfa.definition,
    };

    const { totalCount } = await SystemApi
      .fetch(null, 1, null, null, null, [SystemFilters.createdOnOrBefore(moment().subtract(1, 'months').endOf('month')), this.systemMfaFilter]);
    dataObject.value = totalCount;

    return dataObject;
  }

  fetchUsersInMfa = async () => {
    const dataObject = {
      type: valueReportDefinitions.securityCompliance.userMfa.type,
      label: valueReportDefinitions.securityCompliance.userMfa.label,
      count: 0,
      total: 0,
      definition: valueReportDefinitions.securityCompliance.userMfa.definition,
    };

    dataObject.total = this.totalUsers;

    const { totalCount } = await SystemUserApi
      .fetch(null, 1, null, null, null, [SystemUserFilters.createdOnOrBefore(moment().subtract(1, 'months').endOf('month')), this.userMfaFilter]);
    dataObject.count = totalCount;

    return dataObject;
  }

  fetchAuthsUsingMfa = async () => {
    const dataObject = {
      type: valueReportDefinitions.securityCompliance.authenticationMfa.type,
      label: valueReportDefinitions.securityCompliance.authenticationMfa.label,
      value: 0,
      definition: valueReportDefinitions.securityCompliance.authenticationMfa.definition,
    };
    let totalAuths = 0;
    let mfaAuths = 0;

    const totalAuthEvents = await this.getInsightsEventsCount(
      this.reportPeriodStartDate,
      this.reportPeriodEndDate,
      [EventTypes.userLoginAttempt, EventTypes.adminLoginAttempt],
    );
    totalAuths = totalAuthEvents;

    const totalMfaAuthEvents = await this.getInsightsEventsCount(
      this.reportPeriodStartDate,
      this.reportPeriodEndDate,
      [EventTypes.userLoginAttempt, EventTypes.adminLoginAttempt],
      [{ mfa: 'true' }],
    );
    mfaAuths = totalMfaAuthEvents;

    // Check that we are producing a percentage that makes sense (1 - 100%)
    if (totalAuths > 0 && mfaAuths > 0 && mfaAuths <= totalAuths) {
      dataObject.value = (mfaAuths / totalAuths) * 100;
    }

    // Default values greater than zero but less than one to 1%
    if (dataObject.value > 0 && dataObject.value < 1) {
      dataObject.value = 1;
    } else {
      dataObject.value = Math.round(dataObject.value);
    }

    return dataObject;
  }

  fetchDevicesInMdm = async () => {
    const dataObject = {
      type: valueReportDefinitions.securityCompliance.appleMdm.type,
      label: valueReportDefinitions.securityCompliance.appleMdm.label,
      count: 0,
      total: 0,
      definition: valueReportDefinitions.securityCompliance.deviceMfa.definition,
    };

    const { totalCount: mdmCount } = await SystemApi
      .fetch(null, 1, null, null, null, [SystemFilters.createdOnOrBefore(moment().subtract(1, 'months').endOf('month')), this.systemMdmFilter]);
    dataObject.count = mdmCount;

    const { totalCount: macSystemCount } = await SystemApi
      .fetch(null, 1, null, null, null, [SystemFilters.createdOnOrBefore(moment().subtract(1, 'months').endOf('month')), SystemFilters.os({ os: macOSX })]);
    dataObject.total = macSystemCount;

    return dataObject;
  }

  fetchTotalUsersActivated = async () => {
    const dataObject = {
      type: valueReportDefinitions.efficiency.totalUserActivations.type,
      label: valueReportDefinitions.efficiency.totalUserActivations.label,
      value: 0,
      definition: valueReportDefinitions.efficiency.totalUserActivations.definition,
    };

    const totalCreated = await this.getInsightsEventsCount(
      this.reportPeriodStartDate,
      this.reportPeriodEndDate,
      [EventTypes.userCreate],
    );

    const totalActivated = await this.getInsightsEventsCount(
      this.reportPeriodStartDate,
      this.reportPeriodEndDate,
      [EventTypes.userActivated],
    );

    dataObject.value = totalCreated + totalActivated;

    return dataObject;
  }

  fetchTotalApplicationsForReportPeriod = async () => {
    const dataObject = {
      label: valueReportDefinitions.overviewSection.applications.label,
      value: 0,
    };

    // created date filter not allowed so get now total and balance back to report period
    // end date with insights events
    const [total, applicationCreateEvents, applicationDeleteEvents] = await Promise.all([
      ApplicationApiV2.fetchTotalCount(),
      this.getInsightsEventsCount(
        this.reportPeriodEndDate,
        this.currentISODate,
        [EventTypes.applicationCreate],
      ),
      this.getInsightsEventsCount(
        this.reportPeriodEndDate,
        this.currentISODate,
        [EventTypes.applicationDelete],
      ),
    ]);

    const netTotal = total - (applicationCreateEvents - applicationDeleteEvents);

    dataObject.value = netTotal;

    return dataObject;
  }

  fetchTotalUsersSuspended = async () => {
    const dataObject = {
      type: valueReportDefinitions.efficiency.totalUserSuspensions.stat,
      label: valueReportDefinitions.efficiency.totalUserSuspensions.label,
      value: 0,
      definition: valueReportDefinitions.efficiency.totalUserSuspensions.definition,
    };

    const totalSuspended = await this.getInsightsEventsCount(
      this.reportPeriodStartDate,
      this.reportPeriodEndDate,
      [EventTypes.userSuspended],
    );

    const totalDeleted = await this.getInsightsEventsCount(
      this.reportPeriodStartDate,
      this.reportPeriodEndDate,
      [EventTypes.userDelete],
    );

    dataObject.value = totalSuspended + totalDeleted;

    return dataObject;
  }

  fetchTotalCommandsForReportPeriod = async () => {
    const dataObject = {
      label: valueReportDefinitions.overviewSection.commands.label,
      value: 0,
    };

    // commands have no created date so we have to get today's todal and balance back to
    // report period start date with command_create/command_delete events
    const [total, commandCreateEvents, commandDeleteEvents] = await Promise.all([
      CommandsApi.fetchTotalCount(),
      this.getInsightsEventsCount(
        this.reportPeriodEndDate,
        this.currentISODate,
        [EventTypes.commandCreate],
      ),
      this.getInsightsEventsCount(
        this.reportPeriodEndDate,
        this.currentISODate,
        [EventTypes.commandDelete],
      ),
    ]);

    const netTotal = total - (commandCreateEvents - commandDeleteEvents);

    dataObject.value = netTotal;

    return dataObject;
  }

  fetchTotalDeviceGroupsForReportPeriod = async () => {
    const { systemGroup } = DirectoryGroupingConstants.groupTypes;
    const dataObject = {
      label: valueReportDefinitions.overviewSection.deviceGroups.label,
      value: 0,
    };

    const [total, systemGroupCreateEvents, systemGroupDeleteEvents] = await Promise.all([
      SystemGroupApi.fetchTotalCount(),
      this.getInsightsEventsCount(
        this.reportPeriodEndDate,
        this.currentISODate,
        [EventTypes.groupCreate],
        [{ [EventMetadata[eventFields.resourceType].path]: systemGroup }],
      ),
      this.getInsightsEventsCount(
        this.reportPeriodEndDate,
        this.currentISODate,
        [EventTypes.groupDelete],
        [{ [EventMetadata[eventFields.resourceType].path]: systemGroup }],
      ),
    ]);

    const netTotal = total - (systemGroupCreateEvents - systemGroupDeleteEvents);

    dataObject.value = netTotal;

    return dataObject;
  }

  fetchAppIdentitiesProvisioned = async () => {
    const dataObject = {
      type: valueReportDefinitions.efficiency.scim.type,
      label: valueReportDefinitions.efficiency.scim.label,
      value: 0,
      definition: valueReportDefinitions.efficiency.scim.definition,
    };

    dataObject.value = await this.getInsightsEventsCount(
      this.reportPeriodStartDate,
      this.reportPeriodEndDate,
      [EventTypes.userCreateProvision],
    );

    return dataObject;
  }

  fetchTotalPoliciesForReportPeriod = async () => {
    const dataObject = {
      label: valueReportDefinitions.overviewSection.policies.label,
      value: 0,
    };

    const [total, policiesCreateEvents, policiesDeleteEvents] = await Promise.all([
      PolicyApi.fetchTotalCount(),
      this.getInsightsEventsCount(
        this.reportPeriodEndDate,
        this.currentISODate,
        [EventTypes.policyCreate],
      ),
      this.getInsightsEventsCount(
        this.reportPeriodEndDate,
        this.currentISODate,
        [EventTypes.policyDelete],
      ),
    ]);

    const netTotal = total - (policiesCreateEvents - policiesDeleteEvents);

    dataObject.value = netTotal;

    return dataObject;
  }

  fetchScheduledCommands = async () => {
    const dataObject = {
      type: valueReportDefinitions.efficiency.recurringCommands.type,
      label: valueReportDefinitions.efficiency.recurringCommands.label,
      value: 0,
      definition: valueReportDefinitions.efficiency.recurringCommands.definition,
    };
    // we don't store command create date, so there is nothing to filter on, return all
    const commands = await CommandsApi.fetchAll();

    if (commands && commands.length > 0) {
      commands.forEach(command => {
        if (command.schedule !== 'immediate') {
          dataObject.value += 1;
        }
      });
    }

    return dataObject;
  }

  fetchTotalDevicesDataForReportPeriod = async () => {
    const dataObject = {
      label: valueReportDefinitions.overviewSection.devices.label,
      value: 0,
    };

    dataObject.value = this.totalDevices;

    return dataObject;
  }

  fetchTotalUsersDataForReportPeriod = async () => {
    const dataObject = {
      label: valueReportDefinitions.overviewSection.users.label,
      value: 0,
    };

    dataObject.value = this.totalUsers;

    return dataObject;
  }

  fetchTotalUserGroupsForReportPeriod = async () => {
    const { userGroup } = DirectoryGroupingConstants.groupTypes;
    const dataObject = {
      label: valueReportDefinitions.overviewSection.userGroups.label,
      value: 0,
    };

    const [total, userGroupCreateEvents, userGroupDeleteEvents] = await Promise.all([
      UserGroupApi.fetchTotalCount(),
      this.getInsightsEventsCount(
        this.reportPeriodEndDate,
        this.currentISODate,
        [EventTypes.groupCreate],
        [{ [EventMetadata[eventFields.resourceType].path]: userGroup }],
      ),
      this.getInsightsEventsCount(
        this.reportPeriodEndDate,
        this.currentISODate,
        [EventTypes.groupDelete],
        [{ [EventMetadata[eventFields.resourceType].path]: userGroup }],
      ),
    ]);

    const netTotal = total - (userGroupCreateEvents - userGroupDeleteEvents);

    dataObject.value = netTotal;

    return dataObject;
  }

  getRadiusServerCount = async () => {
    const total = await RadiusServerApi.fetchTotalCount();
    return total;
  }

  getDirectories = async () => {
    const directories = await DirectoryApi.fetchAll();
    return directories;
  }

  getVendorConsolidationData = async (role) => {
    const vendorConsolidationData = {};
    const {
      getProductCodeByType,
      ProductCodeToSubscriptionType,
      SubscriptionFields,
      SubscriptionTypes,
    } = SubscriptionConstants;
    const productCode = getProductCodeByType(SubscriptionTypes.platformPlus);

    const subscriptions = await SubscriptionApi.fetchAll();
    const defaultSubscription = find(subscriptions, [SubscriptionFields.productCode, productCode]);

    let currentPlan = {};

    if (role && (role === administratorWithBilling || role === commandRunnerWithBilling)) {
      const { data } = await PlanApi.fetch();
      currentPlan = data;
    }

    // Only default the plan name if it's missing
    if (!currentPlan.name) {
      currentPlan.name = 'JumpCloud';
    }

    // if the currect plan is legacy and/or has undefined values in any critical field
    // then default to the Platform Plus plan
    if (
      !currentPlan.pricePerUser
      || currentPlan.pricePerUser < 0
      || !currentPlan.productCode
      || !has(ProductCodeToSubscriptionType, currentPlan.productCode)
    ) {
      currentPlan.pricePerUser = defaultSubscription.listPrice;
      currentPlan.productCode = defaultSubscription.productCode;
    }

    vendorConsolidationData.plan = currentPlan;

    return vendorConsolidationData;
  };
}

export default new ValueReportApiWrapper();
